import React, { useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import ProductPage from './ProductPage';
import CreatorProductsPage from './CreatorProductsPage';
import SiteLoading from '../components/SiteLoading';
import { creatorStorefrontService, productService } from '../services/api';
import {
  dispatchStorefrontSegmentMode,
  isMongoObjectIdString,
  isReservedStorefrontSlug,
} from '../utils/creatorSlug';
import { setPrefetchedPdpProduct } from '../utils/pdpPrefetchCache';

/** Paths that redirect to dedicated routes (legacy ProductPage guard). */
const RESERVED_REDIRECTS = {
  about: '/about',
  contact: '/contact',
  'return-policy': '/return-policy',
  'privacy-policy': '/privacy-policy',
  'payment-methods': '/payment-methods',
};

function StorefrontSegmentResolver({ segment }) {
  const [mode, setMode] = useState('loading');
  const [prefetchedProduct, setPrefetchedProduct] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setMode('loading');
    setPrefetchedProduct(null);

    const run = async () => {
      const profilePromise = creatorStorefrontService
        .getCreatorProfile(segment)
        .catch(() => null);
      const productPromise = productService.getProductByUrl(segment).catch(() => null);

      const productRes = await productPromise;
      if (cancelled) return;

      const product = productRes?.product ?? productRes?.data?.product ?? null;
      if (product) {
        setPrefetchedPdpProduct(segment, product);
        setPrefetchedProduct(product);
        setMode('product');
        dispatchStorefrontSegmentMode('product', segment);
        return;
      }

      const profileRes = await profilePromise;
      if (cancelled) return;

      if (profileRes?.success && profileRes?.creator) {
        setMode('shop');
        dispatchStorefrontSegmentMode('shop', segment);
        return;
      }

      setMode('product');
      dispatchStorefrontSegmentMode('product', segment);
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [segment]);

  if (mode === 'loading') {
    return (
      <SiteLoading
        title="Loading product"
        subtitle="Just a moment…"
        icon="👕"
        variant="full"
      />
    );
  }

  if (mode === 'shop') {
    return <CreatorProductsPage legacyCreatorId={segment} />;
  }

  return <ProductPage key={segment} prefetchedProduct={prefetchedProduct} />;
}

/** Bottom catch-all `/:segment` — creator shop (`/umardaraz`) or SEO product slug. */
export default function StorefrontSingleSegmentPage() {
  const { productUrl: slugParam } = useParams();
  const slug = slugParam ? decodeURIComponent(slugParam) : '';

  if (!slug) {
    return <Navigate to="/" replace />;
  }

  const key = slug.toLowerCase();
  if (Object.prototype.hasOwnProperty.call(RESERVED_REDIRECTS, key)) {
    return <Navigate to={RESERVED_REDIRECTS[key]} replace />;
  }

  if (isReservedStorefrontSlug(key)) {
    return <Navigate to="/" replace />;
  }

  if (isMongoObjectIdString(slug)) {
    dispatchStorefrontSegmentMode('shop', slug);
    return <CreatorProductsPage legacyCreatorId={slug} />;
  }

  return <StorefrontSegmentResolver segment={slug} />;
}
